import type { DetailsData, ProductCardExtended, ProductSetItem, ProductUpsell, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { CartItem, PriceCart } from '#types/cart'
import type { MenuItem } from '#types/components/cms/mega-menu'
import type {
  BloomreachProductObject,
  InteractionOrigin,
  P13nImpressionContext,
  ProductContext,
  ProductObject,
  SetItemObject,
  ShippingMethodContext,
  ShopTheLookContext
} from '#types/gtm'
import type { BloomreachRecommendedProduct } from '#types/p13n'
import type { ProductAttribute } from '#types/product'
import type { RecursivePartial } from '#types/utils'
import { injectHead, isArray } from '#imports'

type ProductUpsellWithAnalyticsId = ProductUpsell & {
  analyticsId?: string
}

const gtmCartProductsMap = useLocalStorage('gtmCartProductsMap', {}, { mergeDefaults: true }).value
const gtmInteractionOrigin = useSessionStorage<InteractionOrigin>('gtmInteractionOrigin', '', { mergeDefaults: true })
let gtmInteractionForcedOrigin = false

/**
 * Maps the internal `InteractionOrigin` enum to the human-readable strings expected by GTM events
 * (e.g. `'recommendation-carousel'` → `'Product Recommendation Carousel'`).
 * @category Utils
 */
export const interactionOriginMap: Record<InteractionOrigin, string> = {
  '': '',
  'ask-me-anything': 'Ask Me Anything',
  'backpack-finder': 'Backpack Finder',
  'bts-game': 'Back to school your way game',
  'cart': 'Cart',
  'customizer': 'Customizer',
  'direct-to-pdp': 'Direct to PDP',
  'explore-collection': 'Explore Collection',
  'fit-analytics': 'Fit Analytics',
  'footwear-finder': 'Footwear Finder',
  'grid': 'Grid',
  'hot-zone': 'HotZone',
  'jacket-finder': 'Jacket Finder',
  'product-bundles': 'Product Bundles',
  'product-compare': 'Product Compare',
  'product-grid': 'Product Grid Component',
  'recommendation-carousel': 'Product Recommendation Carousel',
  'search': 'Search',
  'shoe-finder-retail': 'Shoe Finder Retail',
  'shop-the-look': 'Shop the Look',
  'tent-sleepingbag-guide': 'Tent and Sleepingbag Guide',
  'up-sell-complete-the-look': 'UpSellCompleteTheLook',
}

/**
 * Projects an array of breadcrumb objects to the array of labels GTM expects.
 * @category Utils
 */
export const getPageBreadcrumb = (crumbs) => crumbs?.map(({ label }) => label) || undefined

/**
 * Joins breadcrumb labels with `,` for storage in `history.state` / sessionStorage where structured
 * objects can't be persisted.
 * @category Utils
 */
export const serializeAnalyticsBreadcrumbs = (crumbs) => crumbs.map(({ label }) => label).join(',')

/**
 * Inverse of {@link serializeAnalyticsBreadcrumbs} - turns a comma-joined string back into the
 * array of `{ label }` objects.
 * @category Utils
 */
export const deserializeAnalyticsBreadcrumbs = (serializedCrumbs) => serializedCrumbs?.split(',').map((label) => ({ label }))

/**
 * Resolves the current page title by reading the `<title>` tag from Unhead's resolved tags.
 * Returns an empty string when no title tag is present.
 * @returns The current page title (or `''`).
 * @category Utils
 */
export const getPageTitle = async () => {
  const titleTag = (await injectHead()?.resolveTags())?.find(({ tag }) => tag === 'title')
  return titleTag?.textContent || ''
}

const getColorAttributes = (product: CartItem) => {
  const defaultColor = getDefaultColorOption(product.attributes, product.id)
  const colorId = (defaultColor?.id || product.masterId)?.slice(-3)

  return {
    colorId,
    colorLabel: defaultColor?.label || product.colorDescription,
    id: defaultColor?.id
  }
}

/**
 * Retrieves the persisted GTM context for a product (rating, breadcrumbs, list, interaction
 * origin, …) from `localStorage`. Used to enrich subsequent cart / checkout events with the
 * context captured when the product was first surfaced.
 * @category Utils
 */
export const getGTMProduct = (id: string): any => gtmCartProductsMap[id]

const findGTMProduct = (product: Record<any, any>): any => gtmCartProductsMap[product.masterId || product.id]
  || Object.values(gtmCartProductsMap).find(
    (item: any) =>
      (product.masterId && item.masterId === product.masterId) || (product.id && item.productId === product.id)
  )

/**
 * Builds the GTM `list` string for a recommendation-carousel impression - formatted as
 * `<placement> | Product Recommendation Carousel[: <title>]`. Defaults `placement` to `'Home'`.
 * @category Utils
 */
export const getRecommendationListDescription = ({
  placement,
  title,
}: {
  placement?: string
  title?: string
}): string => {
  const list = `${placement || 'Home'} | Product Recommendation Carousel`
  return title ? `${list}: ${title}` : list
}

/**
 * Derives the GTM `list` description for a product impression by combining its interaction origin,
 * the page breadcrumbs, and (for grid/search) the search term or category trail.
 * Recommendation-carousel impressions short-circuit and reuse the captured `list` instead.
 * @param ctx - The impression context.
 * @param ctx.breadcrumbs - Page breadcrumb trail joined as a string.
 * @param ctx.searchTerm - Search term that produced the impression (PLP/search).
 * @param ctx.appSource - The interaction origin / app source for the impression.
 * @param ctx.productId - Product id used to look up the persisted GTM context.
 * @returns The descriptive `list` string for the GTM event.
 * @category Utils
 */
export const getListDescription = ({ breadcrumbs, searchTerm = '', appSource = '', productId = '' }): string => {
  const gtmProduct = findGTMProduct({ masterId: productId, id: productId })
  const interactionOrigin = appSource || gtmProduct?.interactionOrigin || gtmInteractionOrigin.value || 'direct-to-pdp'
  const isProductList = ['grid', 'search'].includes(interactionOrigin)
  const interactionTitle = interactionOriginMap[interactionOrigin]
  const pageBreadcrumbs = gtmProduct?.breadcrumbs?.map(({ label }) => label).join(' - ') || breadcrumbs
  const pageSearchTerm = gtmProduct?.searchTerm || searchTerm

  if (['recommendation-carousel', 'product-grid'].includes(interactionOrigin)) return gtmProduct?.list

  return `${interactionTitle}${isProductList ? `: PLP | ${pageSearchTerm || pageBreadcrumbs}` : ''}`
}

const getAvailableSizes = (attributes: ProductAttribute[]) => {
  const options = attributes?.find((attribute) => attribute.label === 'Size')?.options
  return options?.filter((size) => size.available).map((size) => size.label) || []
}

const getSelectedVariants = (attributes) => {
  let counter = 0
  const selectedAttributes = isArray(attributes)
    ? attributes
    : Object.entries(attributes).map(([code, value]) =>
        isObject(value)
          ? value
          : { code, value }
      )

  return selectedAttributes
    .reduce((obj, { code, value }) => {
      if (code !== 'color')
        obj[`sizeCode${++counter}`] = value
      return obj
    }, { sizeCode1: '', sizeCode2: '', sizeCode3: '' })
}

const getDiscountTypesByPrice = (price: Partial<PriceCart>): string | undefined => {
  const discountTypes: string[] = []
  const current = price.proratedPrice === 0 ? 0 : (price.highCurrent || price.current || 0)
  const original = price.highOriginal || price.original || 0

  current === 0 && discountTypes.push('Free Gift')
  current > 0 && current < original && discountTypes.push('Marked Down')
  return discountTypes.length ? discountTypes.join(',') : undefined
}

/**
 * Persists the analytics context (rating, breadcrumbs, badge, search term, interaction origin, …)
 * for a product into `localStorage` keyed by both the supplied `id` and the product's own id.
 * Used so add-to-cart / checkout events later in the funnel can attribute the right list / source.
 * @param id - Storage key (typically the master id used by the cart).
 * @param product - The product object to capture context from.
 * @param force - When true, ignores any previously stored context for this product.
 * @category Utils
 */
export const setGtmCartProductsMap = (id: string, product, force?: boolean) => {
  const existingContext = !force ? findGTMProduct(product) as any : {}

  const persistentValues = {
    productId: product.id,
    avgRating: product.rating?.score || 0,
    availSizes: getAvailableSizes(product.attributes),
    historicalCategory: history.state.category,
    lowOnStock: product.eyebrow?.id?.toUpperCase() === 'LOW STOCK',
    badge: product.badge?.label || product.eyebrow?.label,
    breadcrumbs: existingContext?.breadcrumbs
      || deserializeAnalyticsBreadcrumbs(history.state.breadcrumbs),
    bundleId: existingContext?.bundleId || product?.bundleId,
    productBreadcrumbs: product.breadcrumbs,
    numReviews: product.rating?.reviewsCount || 0,
    searchTerm: existingContext?.searchTerm || (history.state.searchTerm ?? history.state.suggestedPhrase),
    interactionOrigin: existingContext?.interactionOrigin || gtmInteractionOrigin.value,
    list: existingContext?.list || product.list,
    placement: existingContext?.placement || history.state.placement
  }

  gtmCartProductsMap[id] = persistentValues
  gtmCartProductsMap[product.id] = persistentValues
}

/**
 * Patches the persisted GTM context for a specific variant with optional analytics fields.
 * Each provided field is merged with the existing context; missing fields are left untouched.
 * @param variantId - The variant's id used as the storage key.
 * @param patch - Optional analytics fields to merge.
 * @param patch.appSource - Interaction origin (mapped through `interactionOriginMap`).
 * @param patch.category - GTM category to record on the variant.
 * @param patch.list - GTM list to record on the variant.
 * @param patch.stockStatus - Stock-status string to record on the variant.
 * @param patch.stockQuantity - Stock quantity to record on the variant.
 * @category Utils
 */
export const setGtmCartProductVariant = (
  variantId: string,
  { appSource, category, list, stockStatus, stockQuantity }:
  { appSource?: InteractionOrigin, category?: string, list?: string, stockStatus?: string, stockQuantity?: number } = {}
) => {
  gtmCartProductsMap[variantId] = {
    ...gtmCartProductsMap[variantId],
    ...(appSource && { interactionOrigin: interactionOriginMap[appSource] }),
    ...(category && { category }),
    ...(list && { list }),
    ...(stockStatus && { stockStatus }),
    ...(stockQuantity && { stockQuantity })
  }
}

/**
 * Removes the persisted GTM context for a cart entry. Also drops the product-id-keyed entry when
 * no other cart items still reference that product id.
 * @param id - The cart entry's id (typically the master id).
 * @param productId - The underlying product id.
 * @category Utils
 */
export const removeGtmCartProductsMap = (id: string, productId: string) => {
  delete gtmCartProductsMap[id]
  if (Object.values(gtmCartProductsMap).filter((gtmProduct: any) => gtmProduct.productId === productId).length === 1)
    delete gtmCartProductsMap[productId]
}

/**
 * Resolves and stores the current GTM interaction origin in `sessionStorage`. Resolution order:
 * (1) the previously stored interaction for this product, (2) inferred from `history.state`
 * (`searchTerm` → `'search'`, `breadcrumbs` → `'grid'`) unless `force` is true, (3) the explicit
 * `interaction` argument, falling back to `'direct-to-pdp'`.
 * @param interaction - Explicit interaction origin (used in priority 3).
 * @param force - When true, skips the history-state inference in priority 2.
 * @category Utils
 */
export const setInteractionOrigin = (interaction?: InteractionOrigin, force?: boolean) => {
  // Track whether this was a forced override (for other GTM logic)
  gtmInteractionForcedOrigin = !!force

  const historyState = history.state
  const productId = historyState?.pageKey

  // Priority 1: Use existing stored context for this product (from previous ATC)
  const existingContext = productId && findGTMProduct({ masterId: productId, id: productId })
  if (existingContext?.interactionOrigin) {
    return gtmInteractionOrigin.value = existingContext.interactionOrigin
  }

  // Priority 2: Infer from browser history (unless forced override)
  // User came from search results or category grid
  if (!force && (historyState.searchTerm || historyState.breadcrumbs)) {
    return gtmInteractionOrigin.value = historyState.searchTerm ? 'search' : 'grid'
  }

  // Priority 3: Use explicit parameter or default
  gtmInteractionOrigin.value = interaction || 'direct-to-pdp'
}

/** Clears the persisted interaction origin in `sessionStorage`. @category Utils */
export const resetInteractionOrigin = () => (gtmInteractionOrigin.value = '')
/** Returns the currently stored interaction origin. @category Utils */
export const getInteractionOrigin = () => gtmInteractionOrigin.value

const getCustomItemsAttributes = (product) => {
  const { enableCustoms } = useFeatureFlags()
  const recipe = product.url && new URL(product.url, location.origin).searchParams.get('recipe')

  return enableCustoms
    ? {
        customized: !!recipe || isCustomsProduct(product) || !!product.recipeId,
        preCreatedCustomCode:
          ((recipe || product.dummyCustoms) && product.id)
          || product.precreatedCustomsCode
          || undefined,
        recipeID: recipe || product.customsRecipeID || product.recipeId || undefined
      }
    : {}
}

/*
  Due to specific financial reporting needs, "price" shall reflect prorated
  amounts which includes cart-level discounts. All other attributes shall
  only reflect product-level pricing and discounts (GLOBAL15-56246)
*/
const getCartProductObject = (product, context) => {
  const price = product.price
  const totalPrice = Math.min(price.rowTotal, price.priceAfterItemDiscount, price.priceAfterOrderDiscount)
  const saleDiscountAmount: number = (price.original - price.current) * product.qty
  return {
    onSale: !!saleDiscountAmount,
    originalPrice: +product.price.original,
    price: (totalPrice - product.taxTotal) / product.qty,
    quantity: context.quantity || product.qty,
    is_prime: product.isPrimeIntended || false,
    saleDiscountAmount,
    taxTotal: product.taxTotal,
    vf_itemID: product.productId,
    virtualStyleCode: product.masterId || product.id,
    ...getSelectedVariants(product.variants),
    ...getCustomItemsAttributes(product)
  }
}

const getProductPrice = (product, selectedVariant) => {
  if (selectedVariant?.maxDigitalProductAmount) {
    return {
      highCurrent: selectedVariant.maxDigitalProductAmount,
      highOriginal: selectedVariant.maxDigitalProductAmount
    }
  }

  if (selectedVariant?.price) {
    return {
      highCurrent: selectedVariant.price.current ?? selectedVariant.price.original,
      highOriginal: selectedVariant.price.original
    }
  }

  return product.price
}

const productInventoryStateStockStatus = {
  InStock: 'AVAILABLE',
  OutOfStock: 'OUT OF STOCK',
  SoldOut: 'SOLD OUT'
} satisfies Record<NonNullable<ProductVariants[number]['productInventoryState']>, NonNullable<ProductContext['stockStatus']>>

/**
 * Resolves the analytics stock status from inventory data, with the product inventory state as a fallback.
 * @param stockStatus - Stock status returned by the inventory endpoint.
 * @param productInventoryState - Availability state carried by the selected product variant.
 * @returns The resolved analytics stock status, when available.
 * @category Utils
 */
export const getProductStockStatus = (
  stockStatus: ProductContext['stockStatus'],
  productInventoryState: ProductVariants[number]['productInventoryState']
) => stockStatus ?? (productInventoryState ? productInventoryStateStockStatus[productInventoryState] : undefined)

const getProductObject = (product, context) => {
  const price = getProductPrice(product, context.selectedVariant)

  return {
    avgRating: product.rating?.score || 0,
    availSizes: getAvailableSizes(product.attributes),
    numReviews: product.rating?.reviewsCount || 0,
    onSale: price.highCurrent !== price.highOriginal,
    originalPrice: +price.highOriginal,
    price: +price.highCurrent,
    saleDiscountAmount: +(price.highOriginal - price.highCurrent).toFixed(2),
    searchTerm: context.searchTerm,
    ...(context.selectedVariant
      ? {
          variantId: context.selectedVariant.id
        }
      : {}),
    ...getCustomItemsAttributes(product)
  }
}

const getGtmProductBrand = () => `${useRuntimeConfig().public.brand}-${useCountryCode()}`.toUpperCase()

const getShippingTrack = (product: CartItem):
  (Omit<ShippingMethodContext, 'shippingWindow'> & { shippingWindow: string }) | undefined => {
  const shipping = product.shippingOptions?.find(({ selected }) => selected)
  if (!shipping) return
  const shippingGTM = {
    shippingStoreId: undefined,
    shippingWindow: shipping.shippingMethod.deliveryTime,
  }
  if (isPickupOrSts(shipping.shippingMethod.code)) {
    return {
      ...shippingGTM,
      shippingMethod: shipping.shippingMethod.code === 'sts'
        ? 'BOPIS_STS'
        : 'BOPIS_SD',
      shippingStoreId: shipping.storeInfo?.id,
    }
  }

  if (shipping.shippingMethod.code === 'prime') {
    return {
      ...shippingGTM,
      shippingMethod: 'PRIME'
    }
  }
  return {
    ...shippingGTM,
    shippingMethod: 'STH',
  }
}

const getGtmCartProduct = (productData = {}) => Object.entries(productData).reduce((product, [key, value]) => {
  if (value) product[key] = value
  return product
}, {})

/**
 * Builds the array of GTM `product` objects for an impression / cart event. For each input
 * product, merges the product-derived fields (id, name, colour, badge, price/discount, position)
 * with the persisted GTM context (list, category, search term, interaction origin, breadcrumbs)
 * and a per-event trace stored in `sessionStorage.gtmTrace`. Differentiates cart items (financial
 * reporting prices) from listing/detail items.
 * @param products - The products to project.
 * @param context - Per-event context (`searchTerm`, `breadcrumbs`, `category`, `bundleId`, …).
 * @returns Array of GTM-ready product objects (empty when `products` is falsy).
 * @category Utils
 */
export const getProductObjectList
  = (products: CartItem[] | ProductCardExtended[] | DetailsData[], context: ProductContext) =>
    products?.map((product, i) => {
      const trace = JSON.parse(sessionStorage.gtmTrace || '{}')

      const pid = product.productId || product.id
      const gtmCartProduct = findGTMProduct({ id: pid })
      const isStyle = PRODUCT_STYLE_ID_REGEX.test(pid)
      const colorAttributes = getColorAttributes(product)

      const pageBreadcrumbs = getPageBreadcrumb(context?.breadcrumbs || gtmCartProduct?.breadcrumbs || []).join(' - ')
      const productBreadcrumbs
        = getPageBreadcrumb(product.breadcrumbs || gtmCartProduct?.productBreadcrumbs) || []

      const searchTerm = context.searchTerm || gtmCartProduct?.searchTerm
      const interactionOrigin = context.appSource || gtmCartProduct?.interactionOrigin || gtmInteractionOrigin.value
      const interactionOriginDescription = interactionOriginMap[interactionOrigin || 'direct-to-pdp']

      const catalogCategory = productBreadcrumbs[productBreadcrumbs.length - 1]
      const forcedCategory = gtmInteractionForcedOrigin ? interactionOriginDescription : null

      const list = context.list || getListDescription({
        breadcrumbs: pageBreadcrumbs,
        searchTerm,
        appSource: interactionOrigin,
        productId: pid
      })

      const recommendationCategory = interactionOrigin === 'recommendation-carousel' && list

      const category = context.preserveListAndCategory
        ? context.category
        : gtmCartProduct?.category
          || recommendationCategory
          || forcedCategory
          || context?.enCategoryNameWithId
          || context.category
          || interactionOriginDescription
          || pageBreadcrumbs
          || gtmCartProduct?.historicalCategory

      const { styleId, hasProductSeason } = useAppConfig().components.gtm
      const getProductId = () => {
        let id = product.id || product.masterId
        if (isStyle) id = colorAttributes?.id
        else if (context.isCartItem) id = product.precreatedCustomsCode || product.masterId || product.id
        return id
      }

      return {
        badge: product.badge?.label || product.eyebrow?.label,
        bundleId: context.bundleId,
        brand: getGtmProductBrand(),
        colorCode: colorAttributes?.colorId,
        colorDescription: colorAttributes?.colorLabel,
        coupon: product.productPromotions?.map(({ couponCode, promotionId }) => couponCode || promotionId).join('|'),
        couponDiscountAmount: product.productPromotions?.reduce((acc, { price }) => acc + Math.abs(price), 0),
        couponType: product.productPromotions?.map(({ couponCode }) => couponCode ? 'CODE' : 'AUTO').join('|'),
        productPromoCampaignId: product.productPromotions
          ?.map(({ campaignId }) => campaignId)
          .filter(Boolean)
          .join('|'),
        onCoupon: !!product.productPromotions,
        discountType: getDiscountTypesByPrice(getProductPrice(product, context.selectedVariant)),
        id: getProductId(),
        lowOnStock: product.eyebrow?.id?.toUpperCase() === 'LOW STOCK',
        name: product.name?.trim(),
        productNameEng: product.productNameEng?.trim(),
        styleCode: (product.masterId || product.id)?.match(styleId)?.[1],
        list,
        category,
        catalogCategory,
        searchTerm,
        reviewsPageId: context.reviewsPageId,
        stockStatus: context.stockStatus,
        ...(hasProductSeason && product.season ? { season: product.season } : {}),
        ...(!context.omitPosition ? { position: (context.offset || 0) + i + 1 } : {}),
        ...(context.isCartItem
          ? getCartProductObject(product, context)
          : getProductObject(product, context)),
        ...getGtmCartProduct(gtmCartProductsMap[pid]),
        ...getShippingTrack(product),
        ...trace[pid]
      }
    }) || []

/**
 * Projects a BloomReach recommendation product onto the GTM `BloomreachProductObject` shape.
 * Derives the colour code from the BloomReach id format (`<style>:<colour>:<...>`) and reuses
 * `getRecommendationListDescription` for the `list` / `category` fields.
 * @param product - The BloomReach recommendation payload.
 * @param position - 1-based slot position in the carousel.
 * @param context - Optional placement / title used for the list description.
 * @returns The GTM-ready product object.
 * @category Utils
 */
export const getProductRecommendationObject = (
  product: BloomreachRecommendedProduct,
  position: number,
  context?: Partial<P13nImpressionContext>
): BloomreachProductObject => {
  const list = getRecommendationListDescription({
    placement: context?.placement,
    title: context?.title
  })

  return {
    id: product.itemGroupId || product.id,
    name: product.title,
    avgRating: Number.parseFloat(product.stars || '0'),
    brand: getGtmProductBrand(),
    colorCode: product.id.split(':')[2],
    list,
    category: list,
    onSale: product.price !== product.salePrice,
    originalPrice: product.price,
    position,
    price: product.salePrice,
    saleDiscountAmount: product.price - product.salePrice,
    ...getCustomItemsAttributes(product)
  }
}

/**
 * Builds the GTM `setItem` array for a "Shop the Look" product-set impression.
 * Position is 1-based and derived from each item's index in the parent `productSet.products`.
 * @param products - The set items to project.
 * @param ctx - Shop-the-look context.
 * @param ctx.productSet - The parent product set (used for `bundleId` and slot position).
 * @returns Array of GTM-ready set-item objects.
 * @category Utils
 */
export const getProductSetItemObjectList = (
  products: ProductSetItem[],
  { productSet }: ShopTheLookContext
): SetItemObject[] => products.map((setItem) => ({
  id: setItem.id,
  name: setItem.name,
  category: 'Shop the Look',
  list: 'Shop the Look',
  bundleId: productSet.productSetId,
  position: productSet.products!.indexOf(setItem) + 1
}))

/**
 * Projects an upsell ("Complete the Look") product onto a partial GTM `ProductObject` with the
 * `'UpSellCompleteTheLook'` list/category and the colour + style code derived from the upsell SKU.
 * @param upsell - The upsell product payload.
 * @param position - 1-based slot position in the upsell carousel.
 * @returns A partial GTM product object (fields absent from upsell payloads are set to `undefined`).
 * @category Utils
 */
export const getProductUpsellObject = (
  upsell: ProductUpsellWithAnalyticsId,
  position: number
): Partial<ProductObject> => {
  const { styleId } = useAppConfig().components.gtm
  const splitSku = upsell.sku.split(':')
  const masterId = getProductIdFromSku(upsell.sku)

  return {
    avgRating: undefined,
    badge: undefined,
    brand: getGtmProductBrand(),
    bundleId: undefined,
    catalogCategory: undefined,
    category: 'UpSellCompleteTheLook',
    colorCode: splitSku[2],
    colorDescription: undefined,
    discountType: getDiscountTypesByPrice(upsell.price),
    id: upsell.analyticsId || upsell.id,
    list: 'UpSellCompleteTheLook',
    name: upsell.name,
    numReviews: undefined,
    onSale: upsell.price.highCurrent !== upsell.price.highOriginal,
    originalPrice: upsell.price.highOriginal,
    position,
    price: upsell.price.highCurrent,
    saleDiscountAmount: +(upsell.price.highOriginal - upsell.price.highCurrent).toFixed(2),
    searchTerm: undefined,
    styleCode: masterId?.match(styleId)?.[1],
  }
}

/**
 * Formats a category name + URL pair into the slug-and-id form GTM expects:
 * `<lowercased-hyphenated-name>_<trailing-digits-of-url>`.
 * @example
 * formatTrackingCategory('Men’s Jackets', '/men/jackets-12345') // "men's-jackets_12345"
 * @category Utils
 */
export const formatTrackingCategory = (name: string | undefined, url?: string | undefined) =>
  `${name?.toLowerCase().trim().replace(/\s+/g, '-')}_${url?.match(/\d+$/)?.[0] || ''}`

/**
 * Builds a mega-menu GTM tracking label with the correct `L{level} - ` prefix applied by the caller
 * (never trusted from CMS content). Strips any conflicting level prefix that may already be baked
 * into the CMS-authored label (e.g. a mis-tagged `"L3 - ..."` value on an actual L2 node) before
 * re-applying the correct one, so the `eventLabel` hierarchy always matches the real nesting depth.
 * @example
 * formatMegaMenuLevelLabel('Mens Lightweight Jackets', 2) // "L2 - Mens Lightweight Jackets"
 * formatMegaMenuLevelLabel('L3 - Mens Lightweight Jackets', 2) // "L2 - Mens Lightweight Jackets"
 * @category Utils
 */
export const formatMegaMenuLevelLabel = (label: string | undefined, level: 1 | 2 | 3): string =>
  label ? `L${level} - ${label.replace(MEGA_MENU_LEVEL_PREFIX_REGEX, '').replace(MEGA_MENU_TRAILING_COUNT_REGEX, '').trim()}` : ''

/**
 * Extracts and normalises the tracking label from a mega-menu item, resolving
 * field priority (`target.delocalizedTracking` → `title` → `delocalizedTracking` → `name`)
 * and stripping any level prefix or trailing category count.
 * @category Utils
 */
export const getMegaMenuItemLabel = (mi: RecursivePartial<MenuItem> | undefined): string =>
  (mi?.title || mi?.target?.delocalizedTracking || mi?.delocalizedTracking || mi?.name || '')
    .replace(MEGA_MENU_LEVEL_PREFIX_REGEX, '')
    .replace(MEGA_MENU_TRAILING_COUNT_REGEX, '')
    .trim()

/**
 * Strips a CMS level prefix and trailing category count from a raw label string.
 * Use when the caller controls field-priority extraction and only needs the cleaning step.
 * @category Utils
 */
export const cleanMegaMenuLabel = (label: string): string =>
  label.replace(MEGA_MENU_LEVEL_PREFIX_REGEX, '').replace(MEGA_MENU_TRAILING_COUNT_REGEX, '').trim()

/**
 * Creates a handler for accordion expand/close GTM events.
 * Fires `pdpPage.onAccordionInteraction` with the appropriate action based on expanded state.
 * @param gtm - GTM plugin instance from useNuxtApp()
 * @returns Handler function to bind to @close and @expand events
 * @example
 * const { $gtm } = useNuxtApp()
 * const onAccordionInteraction = createAccordionInteractionHandler($gtm)
 * // In template: @close="onAccordionInteraction(label, false)" @expand="onAccordionInteraction(label, true)"
 * @category Utils
 */
export const createAccordionInteractionHandler = (gtm) =>
  (label: string, expanded: boolean) => gtm.push('pdpPage.onAccordionInteraction', label, expanded)
